import api from '../../../utils/api'

// Uploading a template header file from a chat (campaign chat and AI Nudge Creation). The
// component provides `loading` and `sendToBackend({ action })`; the backend is told the name
// the file was saved under in Media Management (or why the upload failed).
export default {
  methods: {
    // A template header file uploaded in the chat goes into Media Management through
    // createMedia, the Upload Media screen's own endpoint (S3 + WhatsApp + Messenger copies,
    // one mapping row per language). The chat backend is JSON-only, so it's sent the name
    // the file was saved under and looks the item up itself. createMedia can fail after
    // saving some copies, so the name goes back on failure too and the backend decides.
    async uploadTemplateMedia (file, config) {
      if (!file) return
      // Blocks a second click while a 16 MB video is still being read; sendToBackend clears it.
      this.loading = true
      try {
        await this.uploadTemplateMediaFile(file, config)
      } catch (err) {
        // Anything unexpected still hands control back to the chat with the reason, rather
        // than leaving it stuck in "loading".
        this.loading = false
        this.sendToBackend({ action: { type: 'media-upload', value: { uploadError: `the upload failed (${err.message || err})` } } })
      }
    },

    async uploadTemplateMediaFile (file, config) {
      // Some browsers give no type for .3gp (or any file); fall back to the extension, and
      // re-wrap the file so createMedia stores and forwards a real type, not octet-stream.
      const BY_EXTENSION = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', mp4: 'video/mp4', '3gp': 'video/3gp', '3gpp': 'video/3gp', pdf: 'application/pdf' }
      const extension = (file.name.split('.').pop() || '').toLowerCase()
      const type = file.type === 'video/3gpp' ? 'video/3gp' : (file.type || BY_EXTENSION[extension] || '')
      const problem = await this.headerFileProblem(file, type, config)
      if (problem) {
        this.sendToBackend({ action: { type: 'media-upload', value: { uploadError: problem } } })
        return
      }
      const upload = file.type ? file : new File([file], file.name, { type })
      const topic = (file.name.replace(/\.[^.]+$/, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'header').slice(0, 30)
      // Time + random, so the name can't land on an existing item: the backend finds the
      // upload by name, and a clash would attach someone else's file.
      const suffix = `${Date.now().toString(36)}${Math.random().toString(16).slice(2, 6)}`
      const mediaName = `${config.namePrefix}_${topic}_${config.typeShort}_${suffix}`
      const formData = new FormData()
      formData.append('media_name', mediaName)
      formData.append('media_type', type)
      // One copy per country (createMedia makes each country's S3 and WhatsApp copies): a
      // template made for several countries needs the file in every one of them.
      const countryIds = config.countryIds && config.countryIds.length ? config.countryIds : [config.countryId]
      countryIds.forEach(countryId => formData.append(`files[${countryId}][${config.languageIds.join(',')}][media]`, upload))
      let uploadError = null
      try {
        // buildRequest resolves 4xx instead of throwing, and respond() wraps the body as
        // { status, payload: { message } }, so both paths are read the same way.
        const { data, status } = await api.buildRequest('createMedia', 'POST', formData, { 'Content-Type': 'multipart/form-data' })
        if (status !== 200) uploadError = (data && data.payload && data.payload.message) || `upload failed (HTTP ${status})`
      } catch (error) {
        const body = error.response && error.response.data
        uploadError = (body && ((body.payload && body.payload.message) || body.message)) || error.message || 'upload failed'
      }
      this.loading = false
      this.sendToBackend({ action: { type: 'media-upload', value: { uploaded: mediaName, uploadError } } })
    },

    // Same checks the server runs again before submitting (templateMedia.js's
    // checkHeaderFile), done here so a bad file never reaches Media Management: type, size,
    // the file's real signature, and for video WhatsApp's H.264 + AAC rule (codec names are
    // four-character codes in the file, so a byte search finds them).
    async headerFileProblem (file, type, config) {
      const mb = bytes => (bytes / (1024 * 1024)).toFixed(1)
      if (!file.size) return `${file.name} is empty`
      if (!config.mimes.includes(type)) return `${file.name} is ${type || 'an unknown type'}, but this header takes ${config.hint}`
      if (file.size > config.maxBytes) return `${file.name} is ${mb(file.size)} MB, over the ${mb(config.maxBytes)} MB limit`
      const isVideo = type.indexOf('video/') === 0
      let bytes
      try {
        bytes = new Uint8Array(await new Response(isVideo ? file : file.slice(0, 16)).arrayBuffer())
      } catch (error) {
        return `${file.name} couldn't be read (${error.message})`
      }
      const ascii = (start, end) => String.fromCharCode.apply(null, bytes.subarray(start, end))
      const signature = bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF ? 'image/jpeg'
        : ascii(0, 4) === '\x89PNG' ? 'image/png'
          : ascii(0, 5) === '%PDF-' ? 'application/pdf'
            : ascii(4, 8) === 'ftyp' ? 'video' : null
      const expected = isVideo ? 'video' : type
      if (signature !== expected) return `${file.name} isn't really ${type} (its contents don't match). Export it again in the right format`
      if (!isVideo) return null
      // The codec codes live in the movie header (the "moov" box). Searching only there,
      // not the whole file, keeps a random 4-byte match in the video data from reading as a
      // codec. Without a readable moov, fall back to the whole file.
      const region = this.moovBox(bytes) || bytes
      const has = code => {
        const c = [0, 1, 2, 3].map(i => code.charCodeAt(i))
        for (let i = 0; i < region.length - 3; i++) {
          if (region[i] === c[0] && region[i + 1] === c[1] && region[i + 2] === c[2] && region[i + 3] === c[3]) return true
        }
        return false
      }
      if (has('hvc1') || has('hev1')) return `${file.name} is H.265/HEVC video. WhatsApp needs H.264, so re-export it as H.264`
      if (has('av01') || has('vp09') || has('s263')) return `${file.name} uses a video codec WhatsApp can't play. Re-export it as H.264 with AAC audio`
      if (!has('avc1') && !has('avc3')) return `${file.name} has no H.264 video track WhatsApp can read. Re-export it as H.264 with AAC audio`
      if (has('soun') && !has('mp4a')) return `${file.name} has audio that isn't AAC. Re-export it with AAC audio (or no audio)`
      return null
    },

    // The top-level "moov" box of an MP4/3GPP file, walking box headers (4-byte size, 4-byte
    // type; size 1 = 64-bit size follows, 0 = to the end). Null if there isn't one.
    moovBox (bytes) {
      const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
      let offset = 0
      while (offset + 8 <= bytes.length) {
        let size = view.getUint32(offset)
        const type = String.fromCharCode(bytes[offset + 4], bytes[offset + 5], bytes[offset + 6], bytes[offset + 7])
        let header = 8
        if (size === 1) {
          if (offset + 16 > bytes.length) return null
          size = view.getUint32(offset + 8) * 4294967296 + view.getUint32(offset + 12)
          header = 16
        } else if (size === 0) {
          size = bytes.length - offset
        }
        if (size < header) return null
        if (type === 'moov') return bytes.subarray(offset, Math.min(offset + size, bytes.length))
        offset += size
      }
      return null
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/nudge-console/templateMediaUpload.js